<template>
  <div class="response">
    <div class="response__box">
      <header class="response__box__header">
        <div class="response__box__header__title">
          {{ $t('response.title') }}
          <span
            class="backoffice-icons response__box__header__title__icon"
            v-tooltip="{
              placement: 'top',
              trigger: 'hover click',
              content: $t('response.tooltip'),
              offset: 3,
            }"
          >
            info
          </span>
        </div>
      </header>
      <div class="response__box__container">
        <div class="response__box__container__no-data" v-if="!currentUseCase">
          {{ $t('noData.noUseCase') }}
        </div>
        <div class="response__box__container__no-responses" v-else-if="currentUseCase && !responses">
          <div v-html="$t('noData.noResponses')"></div>
        </div>
        <template v-else>
          <div class="response__box__container__tabs">
            <div class="response__box__container__tabs__title">
              {{ $t('response.tab.title') }}
            </div>
            <div
              class="response__box__container__tabs__tab positive"
              :class="{
                active: responses && responses.response.PO && responses.response.PO.length,
                selected: currentResponses === 'PO',
              }"
              @click="setResponses('PO')"
            >
              <div class="response__box__container__tabs__tab__text">
                {{ $t('response.tab.positive') }}
              </div>
            </div>
            <div
              class="response__box__container__tabs__tab neutral"
              :class="{
                active: responses && responses.response.NE && responses.response.NE.length,
                selected: currentResponses === 'NE',
              }"
              @click="setResponses('NE')"
            >
              <div class="response__box__container__tabs__tab__text">
                {{ $t('response.tab.neutral') }}
              </div>
            </div>
            <div
              class="response__box__container__tabs__tab negative"
              :class="{
                active: responses && responses.response.NG && responses.response.NG.length,
                selected: currentResponses === 'NG',
              }"
              @click="setResponses('NG')"
            >
              <div class="response__box__container__tabs__tab__text">
                {{ $t('response.tab.negative') }}
              </div>
            </div>
          </div>
          <ul class="response__box__container__list">
            <li
              class="response__box__container__list__item"
              v-for="(response, idx) in responses.response[currentResponses]"
              :key="`response-${idx}`"
            >
              <div class="response__box__container__list__item__color" :class="currentResponses"></div>
              <div class="response__box__container__list__item__text">
                {{ responses.header }}<br />
                {{ response }}<br />
                <strong>{{ responses.footer }}</strong
                ><br />
                <em>{{ responses.promotion }}</em>
              </div>
            </li>
          </ul>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'

export default {
  name: 'Response',
  computed: {
    ...mapState({
      currentUseCase: state => state.data.currentUseCase,
      responses: state => state.data.responses,
      currentResponses: state => state.refine.currentResponses,
    }),
  },
  methods: {
    ...mapActions({
      updateCurrentResponse: 'refine/updateCurrentResponse',
    }),
    setResponses(type) {
      if (this.responses.response[type]) {
        this.updateCurrentResponse(type)
      }
    },
  },
}
</script>

<style lang="scss">
.response {
  width: 100%;

  @media (min-width: $screen-sm) {
    margin-top: $gutter-md;
    height: calc(50% - #{$gutter-md / 2});
  }

  &__box {
    @include box;

    @media (min-width: $screen-sm) {
      height: 100%;
    }

    &__header {
      @include header;

      &__close {
        @include close;
      }

      &__title {
        @include title;

        display: flex;
        align-items: center;

        &__icon {
          margin-left: 12px;
        }
      }
    }

    &__container {
      @include container;

      &__no-responses,
      &__no-data {
        @include noData;

        span {
          font-weight: 800;
        }
      }

      &__tabs {
        display: flex;
        margin-top: -16px;
        border-bottom: 1px solid $generic-color-bg-disabled;
        padding: 16px 0;

        &__title {
          margin-right: 12px;
        }

        &__tab {
          position: relative;
          margin: 0 12px;
          font-weight: 600;

          &.active {
            cursor: pointer;
          }

          &:after {
            position: absolute;
            right: -4px;
            bottom: -16px;
            left: -4px;
            border-top-left-radius: 3px;
            border-top-right-radius: 3px;
            height: 3px;
            content: '';
          }

          &__text {
            opacity: 0.4;

            .active & {
              opacity: 1;
            }
          }

          &.selected {
            &.positive {
              &:after {
                background-color: #059347;
              }
            }
            &.neutral {
              &:after {
                background-color: #e69509;
              }
            }
            &.negative {
              &:after {
                background-color: #a00;
              }
            }
          }

          &:hover {
            &.active:not(.selected) {
              &.positive {
                &:after {
                  opacity: 0.5;
                  background-color: #059347;
                }
              }
              &.neutral {
                &:after {
                  opacity: 0.5;
                  background-color: #e69509;
                }
              }
              &.negative {
                &:after {
                  opacity: 0.5;
                  background-color: #a00;
                }
              }
            }
          }
        }
      }

      &__list {
        margin: 0;
        padding: 0;
        list-style: none;

        &__item {
          display: flex;
          align-items: center;
          border-bottom: 1px solid $generic-color-bg-disabled;
          padding: $gutter-sm 0;

          &:last-child {
            border-bottom: 0;
            padding-bottom: 0;
          }

          &__color {
            margin-right: 12px;
            border-radius: 50%;
            width: 16px;
            height: 16px;

            &.PO {
              background-color: #059347;
            }

            &.NE {
              background-color: #e69509;
            }

            &.NG {
              background-color: #a00;
            }

            &.BL {
              background-color: #565656;
            }
          }

          &__text {
            flex: 1;
          }
        }
      }
    }
  }
}
</style>
